import React from 'react';
import {useTranslation} from 'next-i18next';

// Components
import Thumbnail from '@components/Thumbnail';
import RankProgress from '@components/RankProgress';

import {useLocalizedFormat} from '@deezer/next/contexts/localization';
import {HStack, SimpleGrid, Text, VStack} from '@tempo/core';
import AnalyticCard from '@deezer/next/features/Analytic/components/AnalyticCard';
import type {TopArtist} from '@deezer/next/services/api/Legacy/requests/analytic/types';
import Placeholder from '@components/Placeholder';
import {NavLink} from '@tempo/nextjs';

interface TopArtistsProps {
	topArtists?: TopArtist[];
	isLoading: boolean;
	isError: boolean;
}

type ArtistItemProps = {
	artist: TopArtist;
};

const ArtistItem = ({artist}: ArtistItemProps) => {
	const {t} = useTranslation('d4c');
	const {formatNumber} = useLocalizedFormat();

	return (
		<NavLink
			href={artist.redirectUrl}
			position="relative"
			py={{base: 'spacing.m', md: 'spacing.l'}}
			ps={{base: 'spacing.l', md: 'spacing.xl'}}
			_hover={{
				bg: 'background.neutral.primary.hovered',
			}}
			// Styling for grid dividers is based on https://geary.co/internal-borders-css-grid/
			sx={{
				'&::before, &::after': {
					content: "''",
					position: 'absolute',
					backgroundColor: 'divider.neutral.primary.default',
					zIndex: 1,
				},
				'&::after': {
					inlineSize: '100%',
					blockSize: '1px',
					insetInlineStart: 0,
					insetBlockEnd: '-1px',
				},
				'&::before': {
					inlineSize: '1px',
					blockSize: '100%',
					insetBlockStart: 0,
					insetInlineEnd: '-1px',
				},
			}}
		>
			<HStack spacing={{base: 'spacing.m', md: 'spacing.xl'}}>
				<Text variant="caption.regular" color="text.neutral.primary.default">
					{String(artist.rank).padStart(2, '0')}
				</Text>
				<HStack pe="spacing.s" spacing={{base: 'spacing.s', md: 'spacing.m'}}>
					<Thumbnail
						picture={artist.picture}
						height={{base: '32px', lg: '64px'}}
						width={{base: '32px', lg: '64px'}}
						rounded={true}
						type={artist.type}
					/>
					<VStack alignItems="initial" spacing={0}>
						<Text variant="caption.strong" noOfLines={1}>
							{artist.title}
						</Text>
						<Text
							variant="caption.regular"
							color="text.neutral.secondary.default"
						>
							{t('analyticshome_subtitle_streams_d4c-web')}{' '}
							<strong>{artist.total ? formatNumber(artist.total) : '-'}</strong>
						</Text>
						<RankProgress
							progress={artist.progress}
							justifyContent="flex-start"
						/>
					</VStack>
				</HStack>
			</HStack>
		</NavLink>
	);
};

const TopArtists = ({topArtists, isLoading, isError}: TopArtistsProps) => {
	const {t} = useTranslation('d4c');

	return (
		<AnalyticCard
			hasNoData={!topArtists || !topArtists.length}
			data-testid="top-artists"
			title={t('analyticshome_title_moststreamedartists_d4c-web')}
			isLoading={isLoading}
			isError={isError}
			placeholder={<Placeholder />}
			variant="overview"
		>
			<SimpleGrid columns={{base: 2, md: 4, xl: 5}} overflow="hidden">
				{topArtists?.map(item => (
					<ArtistItem key={item.id} artist={item} />
				))}
			</SimpleGrid>
		</AnalyticCard>
	);
};

export default TopArtists;
